// Card #3517 - drop a cached ride the server no longer returns.
//
// Why this exists: every ride refresh (hydrate, the change check, the ride
// list, the class list) UPSERTS the server's ride rows into the tablet's
// `rides` store and never deletes one. FVD can re-key a ride mid-show (HITS Del
// Mar 2026-10-17: entry 290 moved from class 227.1000 to 227, so the ride row
// became `...sngClassID-227`); the old row then lived on in the tablet's cache
// forever, still tappable offline and still listed by the cache-first reads, so
// a scribe could start a sheet against an id the server no longer has. The
// server holds such a sheet (#3513) and staff can attach it (#3516), but the
// tablet should not create the orphan in the first place.
//
// Rules (what is NOT dropped):
//  - an emergency manual ride (`isManual`) or a not-yet-synced temp ride: the
//    class list's offline overlay and the Add Ride Manually flow depend on the
//    cache holding them, and ridesByClass is an eventually consistent GSI, so a
//    just-created manual ride can be missing from a list that is a moment old.
//  - a ride this tablet already has scoring work against: a local draft sheet
//    (provisional or diverged from its synced ancestor) or any queued outbox
//    op naming it. The tablet NEVER silently moves an already-started sheet to
//    a different ride: it stays on its own ride id, the server holds it, and
//    staff attach it to the live ride (#3516).
// Kiosk rule: nothing here surfaces to the scribe. The dead ride just stops
// being offered; the live ride arrives through the same refresh.
import { isLocalDraftScoreSheet, peekOutbox, rides, scoreSheets } from "./db";

const TEMP_PREFIX = "temp";

function rideIdsNamedByOp(op) {
  const ids = [];
  const p = op?.payload ?? {};
  if (p.rideId) ids.push(p.rideId);
  for (const r of p.rides ?? []) if (r?.rideId) ids.push(r.rideId);
  if (typeof op?.entityKey === "string" && op.entityKey.includes("::")) {
    ids.push(op.entityKey.split("::")[0]);
  }
  return ids;
}

// `liveRides` is the full, successful server answer for `classId`. Returns the
// ids that were dropped from the cache. Never throws: a failure to prune must
// not fail the refresh that called it.
export async function dropDeadRidesForClass(classId, liveRides) {
  try {
    if (!classId || !Array.isArray(liveRides)) return [];
    const live = new Set(liveRides.map((r) => r?.id).filter(Boolean));
    const candidates = (await rides.byClass(classId)).filter(
      (r) =>
        r?.id &&
        !live.has(r.id) &&
        !r.isManual &&
        !String(r.id).startsWith(TEMP_PREFIX),
    );
    if (!candidates.length) return [];

    const protectedIds = new Set();
    for (const op of await peekOutbox()) for (const id of rideIdsNamedByOp(op)) protectedIds.add(id);
    for (const s of await scoreSheets.getAll()) {
      if (s?.rideId && isLocalDraftScoreSheet(s)) protectedIds.add(s.rideId);
    }

    const dropped = [];
    for (const r of candidates) {
      if (protectedIds.has(r.id)) continue;
      await rides.remove(r.id);
      dropped.push(r.id);
    }
    return dropped;
  } catch {
    return [];
  }
}
